import React, { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useDismiss } from '../hooks/useDismiss';
import { DropdownOption, DropdownPanel } from './DropdownPanel';
import { TypingCaret } from './TypingCaret';
type MultiSelectFieldProps = {
  id: string;
  step?: string;
  label: string;
  hideLabel?: boolean;
  smallLabel?: boolean; // small label above a bold underline, as on the Destination field
  endTick?: boolean; // underline turns up at its right end, as on Lufthansa
  // Intro typewriter: text being typed in place of the value, and whether the caret shows
  typed?: string | null;
  typing?: boolean;
  onInteract?: () => void;
  placeholder: string;
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  renderIcon?: (option: string) => React.ReactNode;
};
export function MultiSelectField({
  id,
  step,
  label,
  hideLabel,
  smallLabel,
  endTick,
  typed,
  typing,
  onInteract,
  placeholder,
  options,
  values,
  onChange,
  renderIcon
}: MultiSelectFieldProps) {
  const isTyping = typed !== null && typed !== undefined;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close);
  const toggle = (option: string) => onChange(values.includes(option) ? values.filter(v => v !== option) : [...values, option]);
  const display = values.length === 0 ? placeholder : values.length <= 2 ? values.join(', ') : `${values[0]} + ${values.length - 1} more`;
  return <div ref={rootRef} className="relative" data-id="element-900" data-mp="r81v67qi5rg7">
      <span id={`${id}-label`} className={hideLabel ? 'sr-only' : smallLabel ? 'block text-[12px] font-light text-ink/70' : 'block text-[13px] font-light text-ink/75'} data-id="element-901" data-mp="r7s2gce2p66t">
        {step && <span className="mr-1.5 text-ink/50" data-id="element-902" data-mp="2t1rcyvjdfjig">{step}</span>}
        {label}
      </span>
      <button type="button" id={id} onClick={() => {
      onInteract?.();
      setOpen(o => !o);
    }} aria-haspopup="listbox" aria-expanded={open} aria-labelledby={`${id}-label ${id}`} className={`relative w-full truncate ${hideLabel || smallLabel ? 'border-b-[1.5px]' : 'border-b'} ${endTick ? 'after:absolute after:-bottom-[1.5px] after:right-0 after:h-[11px] after:w-[1.5px] after:bg-ink' : ''} border-ink bg-transparent pb-3 pr-8 pt-1.5 text-left text-[18px] focus:outline-none focus-visible:[box-shadow:0_1px_0_0_#000] ${values.length || isTyping ? 'text-ink' : 'font-light text-ink/60'}`} data-id="element-903" data-mp="2o2ene3147yc6">
        {isTyping ? <span className="inline-flex items-center" data-id="element-904" data-mp="1plpfrvtwmrsv">
            {typed}
            {typing && <TypingCaret data-id="element-905" data-mp="1tmsxyozfczx9" />}
          </span> : display}
        <ChevronDownIcon className={`pointer-events-none absolute ${endTick ? 'right-3' : 'right-0'} top-2.5 h-5 w-5 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.25} aria-hidden="true" data-id="element-906" data-mp="1tky7kv5dwfls" />
      </button>

      <DropdownPanel open={open} data-id="element-907" data-mp="3e3ilspqs7301">
        <ul role="listbox" aria-multiselectable="true" aria-labelledby={`${id}-label`} className="max-h-[300px] overflow-y-auto py-2" data-id="element-908" data-mp="3a2ykrb337f8m">
          {options.map(option => <DropdownOption key={option} label={option} multi icon={renderIcon?.(option)} selected={values.includes(option)} onSelect={() => toggle(option)} data-id="element-909" data-mp="356hyynjd1cyp" />)}
        </ul>
        <div className="flex items-center justify-between border-t border-line px-5 py-3" data-id="element-910" data-mp="3t09ksxwjmcp4">
          <button type="button" onClick={() => onChange([])} className="text-[14px] font-light text-ink/70 transition-opacity duration-150 hover:opacity-60" data-id="element-911" data-mp="3o2hto63q2ulz">
            Clear
          </button>
          <button type="button" onClick={() => setOpen(false)} className="bg-ink px-5 py-2 text-[14px] font-medium text-paper transition-opacity duration-150 hover:opacity-85" data-id="element-912" data-mp="46rt2n2e43rg">
            Done{values.length ? ` (${values.length})` : ''}
          </button>
        </div>
      </DropdownPanel>
    </div>;
}